import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useSearchParams, NavLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import AssignmentIcon from '@mui/icons-material/Assignment';
import QuizIcon from '@mui/icons-material/Quiz';
import GroupsIcon from '@mui/icons-material/Groups';
import TimelineIcon from '@mui/icons-material/Timeline';
import LoginIcon from '@mui/icons-material/Login';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WorkIcon from '@mui/icons-material/Work';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { GitHub, LinkedIn, Language } from '@mui/icons-material';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { Avatar, Tooltip, Chip, LinearProgress } from '@mui/material';
import CopyToClipboard from 'react-copy-to-clipboard';
import { toast } from 'react-hot-toast';
import { jsPDF as JsPDF } from 'jspdf';
import 'jspdf-autotable';
import MainContent from '../components/MainContent';
import Spinner from '../components/spinner/Spinner';
import useGetUserRole from '../hooks/useGetUserRole';
import {
  hasFullStudentDetailAccess,
  maskEmail,
  maskLocation,
  maskPhone,
  maskStudentNumber,
} from '../utils/studentPrivacy';
import TableWrapper from '../utils/TableWrapper';
import useFetchDatav2 from '../hooks/useFetchDatav2';
import useFetchAnalytics from '../hooks/useFetchAnalytics';
import { DoughnutChart } from '../components/charts/DoughnutChart';
import { VerticalBar } from '../components/charts/VerticalBar';
import { LineGraph } from '../components/charts/LineGraph';

const TABS = [
  { key: 'overview', label: 'Overview', icon: <PersonIcon fontSize="small" /> },
  {
    key: 'academics',
    label: 'Academics',
    icon: <SchoolIcon fontSize="small" />,
  },
  {
    key: 'assignments',
    label: 'Assignments',
    icon: <AssignmentIcon fontSize="small" />,
  },
  { key: 'quizzes', label: 'Quizzes', icon: <QuizIcon fontSize="small" /> },
  {
    key: 'progress',
    label: 'Progress',
    icon: <TimelineIcon fontSize="small" />,
  },
  { key: 'groups', label: 'Groups', icon: <GroupsIcon fontSize="small" /> },
  {
    key: 'logins',
    label: 'Login Activity',
    icon: <LoginIcon fontSize="small" />,
  },
  {
    key: 'tickets',
    label: 'Support Tickets',
    icon: <SupportAgentIcon fontSize="small" />,
  },
  {
    key: 'background',
    label: 'Background',
    icon: <WorkIcon fontSize="small" />,
  },
];

function StatCard({ label, value, icon, color = 'persian', subtext }) {
  const colorMap = {
    persian: {
      bg: 'bg-persian-50',
      text: 'text-persian-600',
      border: 'border-persian-200',
    },
    claret: {
      bg: 'bg-red-50',
      text: 'text-claret-500',
      border: 'border-red-200',
    },
    blue: {
      bg: 'bg-blue-50',
      text: 'text-blue-600',
      border: 'border-blue-200',
    },
    green: {
      bg: 'bg-green-50',
      text: 'text-green-600',
      border: 'border-green-200',
    },
    orange: {
      bg: 'bg-orange-50',
      text: 'text-orange-600',
      border: 'border-orange-200',
    },
    purple: {
      bg: 'bg-purple-50',
      text: 'text-purple-600',
      border: 'border-purple-200',
    },
  };
  const c = colorMap[color] || colorMap.persian;
  return (
    <div
      className={`${c.bg} border ${c.border} rounded-xl p-4 flex items-start gap-3`}
    >
      <div className={`${c.text} mt-1`}>{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          {label}
        </p>
        <p className={`text-2xl font-bold ${c.text} mt-0.5`}>{value}</p>
        {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
      </div>
    </div>
  );
}

function SectionHeader({ title, count }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <h3 className="text-lg font-bold text-gray-800">{title}</h3>
      {count !== undefined && (
        <span className="bg-persian-100 text-persian-700 text-xs font-medium px-2 py-0.5 rounded-full">
          {count}
        </span>
      )}
    </div>
  );
}

// ─── HELPER FUNCTIONS (avoid nested ternaries) ─────────────────────────
function getProgressColor(value) {
  if (value >= 100) return '#16a34a';
  if (value >= 50) return '#00999E';
  return '#8B173B';
}

function getProgressTextClass(value) {
  if (value >= 100) return 'text-green-600';
  if (value >= 50) return 'text-persian-500';
  return 'text-claret-500';
}

function formatEndDate(isCurrent, endDate) {
  if (isCurrent) return 'Present';
  if (endDate) {
    return new Date(endDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
    });
  }
  return '';
}

function getLoginActivityColor(daysSinceLastLogin) {
  if (daysSinceLastLogin !== null && daysSinceLastLogin < 7) return 'green';
  return 'claret';
}

function formatLastLoginStatValue(privacyRestricted, daysSinceLastLogin) {
  if (privacyRestricted) return 'Restricted';
  if (daysSinceLastLogin !== null) return `${daysSinceLastLogin}d ago`;
  return 'Never';
}

function formatCertificationStatus(student, privacyRestricted) {
  if (privacyRestricted) return 'Restricted';
  if (student.is_scholarship_certification_approved) {
    return `Approved (${student.certificate_type?.replaceAll('_', ' ') || ''})`;
  }
  return 'Not Approved';
}

/** Avoid nested ternaries: renders spinner → content → fallback */
function renderWithLoading(loading, hasData, content, fallbackMsg) {
  if (loading) return <Spinner />;
  if (hasData) return content;
  return <p className="text-gray-400 text-sm">{fallbackMsg}</p>;
}

function StudentIntelligence() {
  const viewerRole = useGetUserRole();
  const [searchParams, setSearchParams] = useSearchParams();
  const studentIdParam = searchParams.get('id') || '';
  const activeTabParam = searchParams.get('tab') || 'overview';

  const [searchInput, setSearchInput] = useState(studentIdParam);
  const [studentId, setStudentId] = useState(studentIdParam);
  const [activeTab, setActiveTab] = useState(activeTabParam);
  const reportRef = useRef(null);

  const privacyRestricted = !hasFullStudentDetailAccess(viewerRole);

  const visibleTabs = useMemo(() => {
    if (!privacyRestricted) return TABS;
    return TABS.filter((t) =>
      [
        'overview',
        'academics',
        'assignments',
        'quizzes',
        'progress',
        'groups',
      ].includes(t.key)
    );
  }, [privacyRestricted]);

  const effectiveTab = visibleTabs.some((t) => t.key === activeTab)
    ? activeTab
    : 'overview';

  useEffect(() => {
    if (!visibleTabs.some((t) => t.key === activeTab)) {
      setActiveTab('overview');
    }
  }, [visibleTabs, activeTab]);

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = searchInput.trim();
    if (!trimmed) {
      toast.error('Please enter a Student ID, Student Number, or Email');
      return;
    }
    setStudentId(trimmed);
    setSearchParams({ id: trimmed, tab: effectiveTab });
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    if (studentId) {
      setSearchParams({ id: studentId, tab: tabKey });
    }
  };

  // ─── DATA FETCHING ───────────────────────────────────────────────────

  // Step 1: Fetch student profile (accepts UUID, student_number, or email)
  const { data: studentData, isLoading: loadingStudent } = useFetchDatav2(
    ['intel-student', studentId],
    `/users/student/${encodeURIComponent(studentId)}`,
    {},
    "Couldn't fetch student details",
    !!studentId
  );

  const student = studentData?.data?.student;

  // Step 2: Resolve the canonical UUID from the student profile
  // This is critical: the search input may be email or student_number,
  // but all downstream APIs require the UUID.
  const resolvedUuid = student?.id || '';

  const { data: assignmentsData, isLoading: loadingAssignments } =
    useFetchDatav2(
      ['intel-assignments', resolvedUuid],
      `/student-assignment-submission/filter-by-student-id/${resolvedUuid}`,
      {},
      "Couldn't fetch assignments",
      !!resolvedUuid
    );

  const { data: quizData, isLoading: loadingQuizzes } = useFetchDatav2(
    ['intel-quizzes', resolvedUuid],
    `/result/quizes/${resolvedUuid}`,
    {},
    "Couldn't fetch quiz results",
    !!resolvedUuid
  );

  const { data: groupsData, isLoading: loadingGroups } = useFetchDatav2(
    ['intel-groups', resolvedUuid],
    '/group-members',
    { params: { student_id: resolvedUuid } },
    "Couldn't fetch groups",
    !!resolvedUuid
  );

  const { data: progressData, isLoading: loadingProgress } = useFetchAnalytics(
    ['intel-progress', resolvedUuid],
    `/students-progress/${resolvedUuid}`,
    {},
    "Couldn't fetch progress",
    !!resolvedUuid
  );

  const { data: enrolledModulesData } = useFetchDatav2(
    ['intel-enrolled', resolvedUuid],
    `/student-enrolled-modules/${resolvedUuid}`,
    {},
    "Couldn't fetch enrolled modules",
    !!resolvedUuid
  );

  const { data: loginData, isLoading: loadingLogins } = useFetchAnalytics(
    ['intel-logins', resolvedUuid],
    `/raw-data/student-login-activities/${resolvedUuid}`,
    {},
    "Couldn't fetch login trail",
    !!resolvedUuid
  );

  const studentEmail = student?.email;

  const { data: ticketsData, isLoading: loadingTickets } = useFetchDatav2(
    ['intel-tickets', studentEmail],
    '/support/tickets/admin',
    { params: { user_email: studentEmail } },
    "Couldn't fetch support tickets",
    !!studentEmail
  );

  const cohortId = student?.cohort?.id;

  const { data: leaderboardData } = useFetchAnalytics(
    ['intel-leaderboard', resolvedUuid, cohortId],
    `/leaderboard-stats/${resolvedUuid}`,
    { params: { cohort_id: cohortId } },
    "Couldn't fetch leaderboard",
    !!resolvedUuid && !!cohortId
  );

  // ─── COMPUTED VALUES ────────────────────────────────────────────────

  const submissions = assignmentsData?.data?.submissions || [];
  const quizResults = useMemo(
    () => quizData?.data?.student_quiz_results || [],
    [quizData]
  );
  const groups = groupsData?.data?.group_members || [];
  const modules = useMemo(
    () => progressData?.data?.student_progress?.modules || [],
    [progressData]
  );
  const enrolledModules =
    enrolledModulesData?.data?.student_enrolled_modules || [];
  const logins = useMemo(() => loginData?.data || [], [loginData]);
  const tickets = ticketsData?.data?.tickets || [];
  const leaderboard = leaderboardData?.data;

  const overallCompletion = useMemo(() => {
    if (!modules.length) return 0;
    const total = modules.reduce(
      (sum, m) => sum + (Number(m.completion) || 0),
      0
    );
    return (total / modules.length).toFixed(1);
  }, [modules]);

  const quizPassRate = useMemo(() => {
    if (!quizResults.length) return 0;
    const passed = quizResults.filter((q) => q.passed).length;
    return ((passed / quizResults.length) * 100).toFixed(0);
  }, [quizResults]);

  const avgQuizScore = useMemo(() => {
    if (!quizResults.length) return 0;
    const total = quizResults.reduce(
      (sum, q) => sum + (Number(q.percentage_result) || 0),
      0
    );
    return (total / quizResults.length).toFixed(1);
  }, [quizResults]);

  const loginsByMonth = useMemo(() => {
    const months = {};
    logins.forEach((login) => {
      const date = new Date(login.date || login.logged_at || login.created_at);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        '0'
      )}`;
      months[key] = (months[key] || 0) + 1;
    });
    const sorted = Object.entries(months).sort(([a], [b]) =>
      a.localeCompare(b)
    );
    return { labels: sorted.map(([k]) => k), data: sorted.map(([, v]) => v) };
  }, [logins]);

  const moduleCompletionData = useMemo(() => {
    return {
      labels: modules.map((m) => m.module_name?.substring(0, 20) || 'Module'),
      data: modules.map((m) => Number(m.completion) || 0),
    };
  }, [modules]);

  const lastLogin = student?.last_login
    ? new Date(student.last_login).toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'No record';

  const daysSinceLastLogin = student?.last_login
    ? Math.floor(
        (Date.now() - new Date(student.last_login).getTime()) /
          (1000 * 60 * 60 * 24)
      )
    : null;

  const generatePDFReport = () => {
    if (!hasFullStudentDetailAccess(viewerRole)) {
      toast.error('Export is only available to administrators');
      return;
    }
    const doc = new JsPDF();
    const name = `${student?.firstname || ''} ${student?.lastname || ''}`;

    doc.setFontSize(18);
    doc.setTextColor(0, 153, 158);
    doc.text('Student Intelligence Report', 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 28);

    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(`${name}`, 14, 40);
    doc.setFontSize(10);
    doc.text(`Email: ${student?.email || 'N/A'}`, 14, 47);
    doc.text(`Student ID: ${student?.student_number || studentId}`, 14, 53);
    doc.text(`Cohort: ${student?.cohort?.name || 'N/A'}`, 14, 59);
    doc.text(`Country: ${student?.country || 'N/A'}`, 14, 65);
    doc.text(`Last Login: ${lastLogin}`, 14, 71);

    doc.setFontSize(12);
    doc.setTextColor(139, 23, 59);
    doc.text('Summary Metrics', 14, 84);

    doc.autoTable({
      startY: 88,
      head: [['Metric', 'Value']],
      body: [
        ['Enrolled Modules', enrolledModules.length.toString()],
        ['Overall Completion', `${overallCompletion}%`],
        ['Assignments Submitted', submissions.length.toString()],
        ['Quizzes Taken', quizResults.length.toString()],
        ['Quiz Pass Rate', `${quizPassRate}%`],
        ['Avg Quiz Score', `${avgQuizScore}%`],
        ['Groups', groups.length.toString()],
        ['Total Logins', logins.length.toString()],
        ['Support Tickets', tickets.length.toString()],
        ['Gamification Points', (student?.points || 0).toString()],
        [
          'Leaderboard Rank',
          leaderboard?.student_leaderboard?.rank
            ? `#${leaderboard.student_leaderboard.rank}`
            : 'N/A',
        ],
      ],
      theme: 'grid',
      headStyles: { fillColor: [0, 153, 158] },
    });

    if (modules.length) {
      doc.addPage();
      doc.setFontSize(12);
      doc.setTextColor(139, 23, 59);
      doc.text('Module Progress', 14, 20);
      doc.autoTable({
        startY: 24,
        head: [['Module', 'Completion %', 'Type']],
        body: modules.map((m) => [
          m.module_name || 'Module',
          `${Number(m.completion || 0).toFixed(1)}%`,
          m.is_paid_module ? 'Paid' : 'Scholarship',
        ]),
        theme: 'grid',
        headStyles: { fillColor: [0, 153, 158] },
      });
    }

    if (quizResults.length) {
      const currentY = doc.lastAutoTable?.finalY || 20;
      if (currentY > 220) doc.addPage();
      doc.setFontSize(12);
      doc.setTextColor(139, 23, 59);
      doc.text('Quiz Results', 14, currentY > 220 ? 20 : currentY + 10);
      doc.autoTable({
        startY: currentY > 220 ? 24 : currentY + 14,
        head: [['Quiz', 'Score %', 'Passed', 'First Attempt']],
        body: quizResults.map((q) => [
          q.quiz_name || 'Quiz',
          `${q.percentage_result || 0}%`,
          q.passed ? 'Yes' : 'No',
          q.is_first_attempt ? 'Yes' : 'No',
        ]),
        theme: 'grid',
        headStyles: { fillColor: [0, 153, 158] },
      });
    }

    doc.save(
      `Student_Report_${name.replace(/\s+/g, '_')}_${
        new Date().toISOString().split('T')[0]
      }.pdf`
    );
    toast.success('PDF report downloaded');
  };

  // ─── TABLE COLUMNS ──────────────────────────────────────────────────

  const assignmentColumns = [
    {
      title: 'Assignment',
      field: 'assignment.title',
      render: (row) => (
        <p className="font-medium">{row?.assignment?.title || 'Untitled'}</p>
      ),
    },
    {
      title: 'Module',
      field: 'assignment.module_name',
      render: (row) => (
        <p className="text-gray-600 text-sm">
          {row?.assignment?.module_name || '—'}
        </p>
      ),
    },
    {
      title: 'Submission URL',
      field: 'submission_url',
      render: (row) =>
        row?.submission_url ? (
          <a
            href={row.submission_url}
            target="_blank"
            rel="noreferrer"
            className="text-blue-500 hover:underline flex items-center gap-1 text-sm"
          >
            View <OpenInNewIcon sx={{ fontSize: 14 }} />
          </a>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      title: 'Submitted At',
      field: 'created_at',
      render: (row) => (
        <p className="text-gray-500 text-sm">
          {row?.created_at
            ? new Date(row.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })
            : '—'}
        </p>
      ),
    },
  ];

  const quizColumns = [
    {
      title: 'Quiz',
      field: 'quiz_name',
      render: (row) => (
        <p className="capitalize font-medium">{row?.quiz_name}</p>
      ),
    },
    {
      title: 'Score',
      field: 'percentage_result',
      render: (row) => {
        const score = Number(row?.percentage_result) || 0;
        return (
          <div className="flex items-center gap-2">
            <div className="w-16">
              <LinearProgress
                variant="determinate"
                value={Math.min(score, 100)}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#e5e7eb',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: score >= 50 ? '#00999E' : '#8B173B',
                    borderRadius: 4,
                  },
                }}
              />
            </div>
            <span
              className={`text-sm font-bold ${
                score >= 50 ? 'text-persian-500' : 'text-claret-500'
              }`}
            >
              {score}%
            </span>
          </div>
        );
      },
    },
    {
      title: 'Passed',
      field: 'passed',
      render: (row) =>
        row?.passed ? (
          <Chip
            label="Passed"
            size="small"
            sx={{
              backgroundColor: '#dcfce7',
              color: '#166534',
              fontWeight: 600,
            }}
          />
        ) : (
          <Chip
            label="Failed"
            size="small"
            sx={{
              backgroundColor: '#fef2f2',
              color: '#991b1b',
              fontWeight: 600,
            }}
          />
        ),
    },
    {
      title: 'First Attempt',
      field: 'is_first_attempt',
      render: (row) =>
        row?.is_first_attempt ? (
          <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 20 }} />
        ) : (
          <CancelIcon sx={{ color: '#dc2626', fontSize: 20 }} />
        ),
    },
  ];

  const groupColumns = [
    {
      title: 'Group Name',
      field: 'group.name',
      render: (row) => (
        <p className="capitalize font-medium">{row?.group?.name}</p>
      ),
    },
    {
      title: 'Role',
      field: 'role',
      render: (row) => (
        <Chip
          label={row?.role || 'Member'}
          size="small"
          sx={{ backgroundColor: '#ede9fe', color: '#5b21b6', fontWeight: 600 }}
        />
      ),
    },
  ];

  const loginColumns = [
    {
      title: 'Login Time',
      field: 'date',
      render: (row) => (
        <p className="text-sm">
          {new Date(
            row?.date || row?.logged_at || row?.created_at
          ).toLocaleString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      ),
    },
    {
      title: 'Day',
      field: 'day',
      render: (row) => {
        const date = new Date(row?.date || row?.logged_at || row?.created_at);
        return (
          <p className="text-sm text-gray-500">
            {date.toLocaleDateString('en-US', { weekday: 'long' })}
          </p>
        );
      },
    },
  ];

  const ticketColumns = [
    {
      title: 'Subject',
      field: 'subject',
      render: (row) => (
        <p className="font-medium">{row?.subject || 'No Subject'}</p>
      ),
    },
    {
      title: 'Status',
      field: 'status',
      render: (row) => {
        const statusColors = {
          open: { bg: '#fef3c7', color: '#92400e' },
          closed: { bg: '#dcfce7', color: '#166534' },
          pending: { bg: '#fef2f2', color: '#991b1b' },
          resolved: { bg: '#dbeafe', color: '#1e40af' },
        };
        const sc =
          statusColors[row?.status?.toLowerCase()] || statusColors.open;
        return (
          <Chip
            label={row?.status || 'Open'}
            size="small"
            sx={{
              backgroundColor: sc.bg,
              color: sc.color,
              fontWeight: 600,
              textTransform: 'capitalize',
            }}
          />
        );
      },
    },
    {
      title: 'Type',
      field: 'type',
      render: (row) => (
        <p className="text-sm text-gray-600 capitalize">{row?.type || '—'}</p>
      ),
    },
    {
      title: 'Created',
      field: 'created_at',
      render: (row) => (
        <p className="text-sm text-gray-500">
          {row?.created_at
            ? new Date(row.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })
            : '—'}
        </p>
      ),
    },
  ];

  // ─── RENDER ──────────────────────────────────────────────────────────

  return (
    <MainContent full>
      {/* Header */}
      <div className="bg-white px-4 py-4 rounded-lg flex justify-between items-center shadow-sm">
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <NavLink
            to="/"
            className="flex items-center text-gray-600 hover:text-persian-500"
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            <p className="text-sm">Home</p>
          </NavLink>
          <p className="text-sm font-semibold text-persian-600">
            Student Intelligence
          </p>
        </Breadcrumbs>
      </div>

      {/* Search Bar */}
      <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="bg-persian-500 text-white p-2 rounded-lg">
            <SearchIcon />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              Student Intelligence Lookup
            </h2>
            <p className="text-sm text-gray-500">
              Enter a student UUID, student number (PLP-XXX-NNNN-N), or email.
              {privacyRestricted
                ? ' Sensitive contact and identity fields are hidden for your role.'
                : ' Full profile, analytics, and activity trail are available.'}
            </p>
          </div>
        </div>
        <form onSubmit={handleSearch} className="flex gap-3 mt-4">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Enter UUID, Student Number (PLP-XXX-NNNN-N), or Email"
            className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:border-persian-500 focus:outline-none transition-colors font-mono"
          />
          <button
            type="submit"
            className="bg-persian-500 hover:bg-persian-600 text-white px-8 py-3 rounded-xl text-sm font-semibold transition-colors flex items-center gap-2"
          >
            <SearchIcon fontSize="small" />
            Search
          </button>
        </form>
      </div>

      {privacyRestricted && studentId && (
        <div className="mt-4 bg-amber-50 border border-amber-200 text-amber-950 px-4 py-3 rounded-xl text-sm">
          Limited view: contact details, identifiers, and support history are
          restricted. Administrators and Super Admins have full access.
        </div>
      )}

      {/* Results */}
      {studentId && (
        <div className="mt-4 pb-20" ref={reportRef}>
          {loadingStudent && <Spinner />}
          {!loadingStudent && !student && studentId && (
            <div className="bg-white rounded-xl p-16 text-center shadow-sm border border-gray-100">
              <div className="bg-red-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <CancelIcon sx={{ fontSize: 32, color: '#8B173B' }} />
              </div>
              <h3 className="text-xl font-bold text-gray-800">
                Student Not Found
              </h3>
              <p className="text-sm text-gray-500 mt-2">
                No student found with ID:{' '}
                <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">
                  {studentId}
                </span>
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Make sure you&apos;re using the correct UUID format
              </p>
            </div>
          )}
          {!loadingStudent && student && (
            <>
              {/* Student Identity Banner */}
              <div className="bg-gradient-to-r from-persian-500 to-persian-600 rounded-xl p-6 text-white shadow-lg">
                <div className="flex items-center gap-6">
                  <Avatar
                    sx={{
                      height: 90,
                      width: 90,
                      border: '3px solid rgba(255,255,255,0.3)',
                    }}
                    src={privacyRestricted ? undefined : student.profile_image}
                  >
                    <p className="uppercase text-2xl font-bold">
                      {student.firstname?.[0]}
                      {student.lastname?.[0]}
                    </p>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h1 className="text-2xl font-bold capitalize">
                        {student.firstname} {student.middlename || ''}{' '}
                        {student.lastname}
                      </h1>
                      {student.student_number &&
                        (privacyRestricted ? (
                          <span className="bg-white/20 text-white text-xs font-mono px-3 py-1 rounded-full opacity-90">
                            {maskStudentNumber(student.student_number)}
                          </span>
                        ) : (
                          <CopyToClipboard
                            text={student.student_number}
                            onCopy={() => toast.success('Student ID copied')}
                          >
                            <span className="bg-white/20 text-white text-xs font-mono px-3 py-1 rounded-full cursor-pointer hover:bg-white/30 transition flex items-center gap-1">
                              {student.student_number}
                              <ContentCopyIcon sx={{ fontSize: 12 }} />
                            </span>
                          </CopyToClipboard>
                        ))}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-white/80 text-sm">
                      {privacyRestricted ? (
                        <span>{maskEmail(student.email || '')}</span>
                      ) : (
                        <CopyToClipboard
                          text={student.email || ''}
                          onCopy={() => toast.success('Email copied')}
                        >
                          <span className="cursor-pointer hover:text-white transition">
                            {student.email || 'No email'}
                          </span>
                        </CopyToClipboard>
                      )}
                      <span>•</span>
                      <span>
                        {privacyRestricted
                          ? maskPhone(student.phone_number || '')
                          : student.phone_number || 'No phone'}
                      </span>
                      <span>•</span>
                      <span className="capitalize">
                        {privacyRestricted
                          ? maskLocation(student.country, student.town)
                          : `${student.country || 'Unknown location'}${
                              student.town ? `, ${student.town}` : ''
                            }`}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      {student.cohort?.name && (
                        <span className="bg-white/20 px-3 py-0.5 rounded-full">
                          Cohort: {student.cohort.name}
                        </span>
                      )}
                      <span className="bg-white/20 px-3 py-0.5 rounded-full capitalize">
                        {privacyRestricted
                          ? '••• • Age: ••'
                          : `${student.gender || 'Gender N/A'} • Age: ${
                              student.age || 'N/A'
                            }`}
                      </span>
                      <span className="bg-white/20 px-3 py-0.5 rounded-full capitalize">
                        {student.level_of_education || 'Education N/A'}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    {!privacyRestricted && (
                      <button
                        type="button"
                        onClick={generatePDFReport}
                        className="bg-white text-persian-600 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-semibold transition flex items-center gap-2"
                      >
                        <DownloadIcon fontSize="small" />
                        Export PDF
                      </button>
                    )}
                    <div className="flex gap-2 mt-1">
                      {!privacyRestricted && student.portfolio && (
                        <Tooltip title="Portfolio">
                          <a
                            href={student.portfolio}
                            target="_blank"
                            rel="noreferrer"
                            className="bg-white/20 hover:bg-white/30 p-1.5 rounded-lg transition"
                          >
                            <Language sx={{ fontSize: 18 }} />
                          </a>
                        </Tooltip>
                      )}
                      {!privacyRestricted && student.github_account && (
                        <Tooltip title="GitHub">
                          <a
                            href={student.github_account}
                            target="_blank"
                            rel="noreferrer"
                            className="bg-white/20 hover:bg-white/30 p-1.5 rounded-lg transition"
                          >
                            <GitHub sx={{ fontSize: 18 }} />
                          </a>
                        </Tooltip>
                      )}
                      {!privacyRestricted && student.linkedin && (
                        <Tooltip title="LinkedIn">
                          <a
                            href={student.linkedin}
                            target="_blank"
                            rel="noreferrer"
                            className="bg-white/20 hover:bg-white/30 p-1.5 rounded-lg transition"
                          >
                            <LinkedIn sx={{ fontSize: 18 }} />
                          </a>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Stats Strip */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-4">
                <StatCard
                  label="Modules Enrolled"
                  value={enrolledModules.length}
                  icon={<SchoolIcon />}
                  color="persian"
                />
                <StatCard
                  label="Overall Completion"
                  value={`${overallCompletion}%`}
                  icon={
                    Number(overallCompletion) >= 50 ? (
                      <TrendingUpIcon />
                    ) : (
                      <TrendingDownIcon />
                    )
                  }
                  color={Number(overallCompletion) >= 50 ? 'green' : 'claret'}
                />
                <StatCard
                  label="Assignments"
                  value={submissions.length}
                  icon={<AssignmentIcon />}
                  color="blue"
                />
                <StatCard
                  label="Quiz Pass Rate"
                  value={`${quizPassRate}%`}
                  icon={<QuizIcon />}
                  color={Number(quizPassRate) >= 50 ? 'green' : 'orange'}
                  subtext={`${quizResults.length} quizzes taken`}
                />
                <StatCard
                  label="Last Login"
                  value={formatLastLoginStatValue(
                    privacyRestricted,
                    daysSinceLastLogin
                  )}
                  icon={<AccessTimeIcon />}
                  color={getLoginActivityColor(daysSinceLastLogin)}
                  subtext={privacyRestricted ? '—' : lastLogin}
                />
                <StatCard
                  label="Points"
                  value={student.points || 0}
                  icon={<EmojiEventsIcon />}
                  color="purple"
                  subtext={
                    leaderboard?.student_leaderboard?.rank
                      ? `Rank #${leaderboard.student_leaderboard.rank}`
                      : ''
                  }
                />
              </div>

              {/* Tab Navigation */}
              <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex border-b border-gray-200 overflow-x-auto">
                  {visibleTabs.map((tab) => (
                    <button
                      type="button"
                      key={tab.key}
                      onClick={() => handleTabChange(tab.key)}
                      className={`flex items-center gap-1.5 px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                        effectiveTab === tab.key
                          ? 'border-persian-500 text-persian-600 bg-persian-50/50'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {tab.icon}
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="p-5">
                  {/* ─── OVERVIEW TAB ─── */}
                  {effectiveTab === 'overview' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Module Completion Chart */}
                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                          <SectionHeader
                            title="Module Completion"
                            count={modules.length}
                          />
                          {renderWithLoading(
                            loadingProgress,
                            modules.length > 0,
                            <div className="h-[300px] w-full">
                              <VerticalBar
                                title="Completion by Module"
                                label="Completion %"
                                colour="#00999E"
                                labels={moduleCompletionData.labels}
                                datasets={moduleCompletionData.data}
                              />
                            </div>,
                            'No module data available'
                          )}
                        </div>

                        {/* Quiz Performance Chart */}
                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                          <SectionHeader
                            title="Quiz Performance"
                            count={quizResults.length}
                          />
                          {renderWithLoading(
                            loadingQuizzes,
                            quizResults.length > 0,
                            <DoughnutChart
                              title="Pass vs Fail"
                              label="Quizzes"
                              labels={['Passed', 'Failed']}
                              datasets={[
                                quizResults.filter((q) => q.passed).length,
                                quizResults.filter((q) => !q.passed).length,
                              ]}
                              backgroundColor={['#00999E', '#8B173B']}
                              borderColor={['#00999E', '#8B173B']}
                            />,
                            'No quiz data available'
                          )}
                        </div>
                      </div>

                      {/* Login Activity Chart */}
                      {!privacyRestricted && (
                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                          <SectionHeader
                            title="Login Activity Over Time"
                            count={logins.length}
                          />
                          {renderWithLoading(
                            loadingLogins,
                            loginsByMonth.labels.length > 0,
                            <div className="w-full">
                              <LineGraph
                                title="Monthly Login Frequency"
                                label="Logins"
                                colour="#00999E"
                                labels={loginsByMonth.labels}
                                datasets={loginsByMonth.data}
                              />
                            </div>,
                            'No login data available'
                          )}
                        </div>
                      )}

                      {/* Personal Info Grid */}
                      <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                        <SectionHeader title="Personal Information" />
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {[
                            {
                              label: 'Full Name',
                              value: `${student.firstname || ''} ${
                                student.middlename || ''
                              } ${student.lastname || ''}`,
                            },
                            {
                              label: 'Email',
                              value: privacyRestricted
                                ? maskEmail(student.email)
                                : student.email,
                            },
                            {
                              label: 'Phone',
                              value: privacyRestricted
                                ? maskPhone(student.phone_number)
                                : student.phone_number,
                            },
                            {
                              label: 'Country',
                              value: privacyRestricted
                                ? maskLocation(student.country, '')
                                : student.country,
                            },
                            {
                              label: 'Town',
                              value: privacyRestricted
                                ? maskLocation('', student.town)
                                : student.town,
                            },
                            {
                              label: 'Gender',
                              value: privacyRestricted ? '•••' : student.gender,
                            },
                            {
                              label: 'Age',
                              value: privacyRestricted ? '••' : student.age,
                            },
                            {
                              label: 'Education Level',
                              value: privacyRestricted
                                ? '••••'
                                : student.level_of_education,
                            },
                            { label: 'Cohort', value: student.cohort?.name },
                            {
                              label: 'Student Number',
                              value: privacyRestricted
                                ? maskStudentNumber(student.student_number)
                                : student.student_number,
                            },
                            {
                              label: 'Certification Status',
                              value: formatCertificationStatus(
                                student,
                                privacyRestricted
                              ),
                            },
                            {
                              label: 'Gamification Points',
                              value: student.points,
                            },
                          ].map((item) => (
                            <div
                              key={item.label}
                              className="bg-white rounded-lg p-3 border border-gray-100"
                            >
                              <p className="text-xs font-medium text-gray-400 uppercase">
                                {item.label}
                              </p>
                              <p className="text-sm font-semibold text-gray-800 mt-0.5 capitalize">
                                {item.value ?? 'N/A'}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {student.profile_desc && !privacyRestricted && (
                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                          <SectionHeader title="Bio" />
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {student.profile_desc}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ─── ACADEMICS TAB ─── */}
                  {effectiveTab === 'academics' && (
                    <div className="space-y-6">
                      {/* Module Progress from Analytics (same source as old profile) */}
                      <SectionHeader
                        title="Module Progress"
                        count={modules.length}
                      />
                      {renderWithLoading(
                        loadingProgress,
                        modules.length > 0,
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {modules.map((mod) => {
                            const completion = Number(mod.completion) || 0;
                            return (
                              <div
                                key={mod.module_id}
                                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                              >
                                {mod.module_image && (
                                  <img
                                    src={mod.module_image}
                                    alt={mod.module_name}
                                    className="w-full h-32 object-cover"
                                  />
                                )}
                                <div className="p-4">
                                  <p className="font-bold text-gray-800 line-clamp-2">
                                    {mod.module_name || 'Module'}
                                  </p>
                                  <div className="flex items-center gap-2 mt-2">
                                    <Chip
                                      label={
                                        mod.is_paid_module
                                          ? 'Paid'
                                          : 'Scholarship'
                                      }
                                      size="small"
                                      sx={{
                                        backgroundColor: mod.is_paid_module
                                          ? '#fef3c7'
                                          : '#dcfce7',
                                        color: mod.is_paid_module
                                          ? '#92400e'
                                          : '#166534',
                                        fontWeight: 600,
                                        fontSize: 11,
                                      }}
                                    />
                                    {mod.module_category && (
                                      <Chip
                                        label={mod.module_category}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: 11 }}
                                      />
                                    )}
                                  </div>
                                  <div className="mt-3">
                                    <div className="flex justify-between text-xs mb-1">
                                      <span className="text-gray-500">
                                        Progress
                                      </span>
                                      <span
                                        className={`font-bold ${
                                          completion >= 50
                                            ? 'text-persian-500'
                                            : 'text-claret-500'
                                        }`}
                                      >
                                        {completion.toFixed(1)}%
                                      </span>
                                    </div>
                                    <LinearProgress
                                      variant="determinate"
                                      value={Math.min(completion, 100)}
                                      sx={{
                                        height: 6,
                                        borderRadius: 3,
                                        backgroundColor: '#e5e7eb',
                                        '& .MuiLinearProgress-bar': {
                                          backgroundColor:
                                            getProgressColor(completion),
                                          borderRadius: 3,
                                        },
                                      }}
                                    />
                                  </div>
                                  {mod.start_date && (
                                    <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                                      <CalendarTodayIcon
                                        sx={{ fontSize: 12 }}
                                      />
                                      Started:{' '}
                                      {new Date(
                                        mod.start_date
                                      ).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                      })}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>,
                        'No module progress data available'
                      )}

                      {/* Enrolled Modules (from Gateway) */}
                      {enrolledModules.length > 0 && (
                        <>
                          <SectionHeader
                            title="Enrolled Modules"
                            count={enrolledModules.length}
                          />
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {enrolledModules.map((em) => {
                              const moduleProgress = modules.find(
                                (m) => m.module_id === em.module_id
                              );
                              const completion = moduleProgress
                                ? Number(moduleProgress.completion)
                                : 0;
                              return (
                                <div
                                  key={em.id || em.module_id}
                                  className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow"
                                >
                                  <p className="font-bold text-gray-800 line-clamp-2">
                                    {em.module_name || 'Module'}
                                  </p>
                                  <div className="flex items-center gap-2 mt-2">
                                    <Chip
                                      label={
                                        em.is_paid_module
                                          ? 'Paid'
                                          : 'Scholarship'
                                      }
                                      size="small"
                                      sx={{
                                        backgroundColor: em.is_paid_module
                                          ? '#fef3c7'
                                          : '#dcfce7',
                                        color: em.is_paid_module
                                          ? '#92400e'
                                          : '#166534',
                                        fontWeight: 600,
                                        fontSize: 11,
                                      }}
                                    />
                                    {em.module_category && (
                                      <Chip
                                        label={em.module_category}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: 11 }}
                                      />
                                    )}
                                  </div>
                                  <div className="mt-3">
                                    <div className="flex justify-between text-xs mb-1">
                                      <span className="text-gray-500">
                                        Completion
                                      </span>
                                      <span
                                        className={`font-bold ${
                                          completion >= 50
                                            ? 'text-persian-500'
                                            : 'text-claret-500'
                                        }`}
                                      >
                                        {completion.toFixed(1)}%
                                      </span>
                                    </div>
                                    <LinearProgress
                                      variant="determinate"
                                      value={Math.min(completion, 100)}
                                      sx={{
                                        height: 6,
                                        borderRadius: 3,
                                        backgroundColor: '#e5e7eb',
                                        '& .MuiLinearProgress-bar': {
                                          backgroundColor:
                                            getProgressColor(completion),
                                          borderRadius: 3,
                                        },
                                      }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* ─── ASSIGNMENTS TAB ─── */}
                  {effectiveTab === 'assignments' && (
                    <div>
                      <SectionHeader
                        title="Assignment Submissions"
                        count={submissions.length}
                      />
                      {loadingAssignments ? (
                        <Spinner />
                      ) : (
                        <TableWrapper
                          title="Assignment Submissions"
                          data={submissions}
                          columns={assignmentColumns}
                        />
                      )}
                    </div>
                  )}

                  {/* ─── QUIZZES TAB ─── */}
                  {effectiveTab === 'quizzes' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <StatCard
                          label="Quizzes Taken"
                          value={quizResults.length}
                          icon={<QuizIcon />}
                          color="blue"
                        />
                        <StatCard
                          label="Pass Rate"
                          value={`${quizPassRate}%`}
                          icon={<CheckCircleIcon />}
                          color={
                            Number(quizPassRate) >= 50 ? 'green' : 'claret'
                          }
                        />
                        <StatCard
                          label="Avg Score"
                          value={`${avgQuizScore}%`}
                          icon={<TrendingUpIcon />}
                          color="persian"
                        />
                      </div>
                      {loadingQuizzes ? (
                        <Spinner />
                      ) : (
                        <TableWrapper
                          title="Quiz Results"
                          data={quizResults}
                          columns={quizColumns}
                        />
                      )}
                    </div>
                  )}

                  {/* ─── PROGRESS TAB ─── */}
                  {effectiveTab === 'progress' && (
                    <div className="space-y-6">
                      <SectionHeader
                        title="Module-by-Module Progress"
                        count={modules.length}
                      />
                      {renderWithLoading(
                        loadingProgress,
                        modules.length > 0,
                        <>
                          <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                            <div className="h-[350px] w-full">
                              <VerticalBar
                                title="Completion per Module"
                                label="Completion %"
                                colour="#00999E"
                                labels={moduleCompletionData.labels}
                                datasets={moduleCompletionData.data}
                              />
                            </div>
                          </div>
                          <div className="space-y-3">
                            {modules.map((mod) => {
                              const comp = Number(mod.completion) || 0;
                              return (
                                <div
                                  key={mod.module_id}
                                  className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4"
                                >
                                  {mod.module_image && (
                                    <img
                                      src={mod.module_image}
                                      alt=""
                                      className="w-12 h-12 rounded-lg object-cover"
                                    />
                                  )}
                                  <div className="flex-1">
                                    <p className="font-semibold text-gray-800">
                                      {mod.module_name || 'Module'}
                                    </p>
                                    <p className="text-xs text-gray-400 capitalize">
                                      {mod.module_category || ''} •{' '}
                                      {mod.is_paid_module
                                        ? 'Paid'
                                        : 'Scholarship'}
                                    </p>
                                  </div>
                                  <div className="w-48">
                                    <div className="flex justify-between text-xs mb-1">
                                      <span className="text-gray-500">
                                        Completion
                                      </span>
                                      <span
                                        className={`font-bold ${getProgressTextClass(
                                          comp
                                        )}`}
                                      >
                                        {comp.toFixed(1)}%
                                      </span>
                                    </div>
                                    <LinearProgress
                                      variant="determinate"
                                      value={Math.min(comp, 100)}
                                      sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor: '#e5e7eb',
                                        '& .MuiLinearProgress-bar': {
                                          backgroundColor:
                                            getProgressColor(comp),
                                          borderRadius: 4,
                                        },
                                      }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </>,
                        'No progress data available'
                      )}
                    </div>
                  )}

                  {/* ─── GROUPS TAB ─── */}
                  {effectiveTab === 'groups' && (
                    <div>
                      <SectionHeader
                        title="Group Memberships"
                        count={groups.length}
                      />
                      {loadingGroups ? (
                        <Spinner />
                      ) : (
                        <TableWrapper
                          title="Groups"
                          data={groups}
                          columns={groupColumns}
                        />
                      )}
                    </div>
                  )}

                  {/* ─── LOGIN ACTIVITY TAB ─── */}
                  {effectiveTab === 'logins' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <StatCard
                          label="Total Logins"
                          value={logins.length}
                          icon={<LoginIcon />}
                          color="blue"
                        />
                        <StatCard
                          label="Last Login"
                          value={
                            daysSinceLastLogin !== null
                              ? `${daysSinceLastLogin} days ago`
                              : 'Never'
                          }
                          icon={<AccessTimeIcon />}
                          color={getLoginActivityColor(daysSinceLastLogin)}
                          subtext={lastLogin}
                        />
                        <StatCard
                          label="Active Months"
                          value={loginsByMonth.labels.length}
                          icon={<CalendarTodayIcon />}
                          color="purple"
                        />
                      </div>
                      {loginsByMonth.labels.length > 0 && (
                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                          <div className="w-full">
                            <LineGraph
                              title="Login Frequency by Month"
                              label="Logins"
                              colour="#00999E"
                              labels={loginsByMonth.labels}
                              datasets={loginsByMonth.data}
                            />
                          </div>
                        </div>
                      )}
                      <div className="mt-6">
                        {loadingLogins ? (
                          <Spinner />
                        ) : (
                          <TableWrapper
                            title="Login Trail"
                            data={logins}
                            columns={loginColumns}
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {/* ─── TICKETS TAB ─── */}
                  {effectiveTab === 'tickets' && (
                    <div>
                      <SectionHeader
                        title="Support Tickets"
                        count={tickets.length}
                      />
                      {loadingTickets ? (
                        <Spinner />
                      ) : (
                        <TableWrapper
                          title="Support Tickets"
                          data={tickets}
                          columns={ticketColumns}
                        />
                      )}
                    </div>
                  )}

                  {/* ─── BACKGROUND TAB ─── */}
                  {effectiveTab === 'background' && (
                    <div className="space-y-6">
                      {/* Education */}
                      <div>
                        <SectionHeader
                          title="Education Background"
                          count={student.education_backgrounds?.length}
                        />
                        {student.education_backgrounds?.length > 0 ? (
                          <div className="space-y-3">
                            {student.education_backgrounds.map((edu) => (
                              <div
                                key={edu.id}
                                className="bg-white border border-gray-200 rounded-xl p-4 flex items-start gap-4"
                              >
                                <div className="bg-blue-50 p-2 rounded-lg">
                                  <SchoolIcon sx={{ color: '#2563eb' }} />
                                </div>
                                <div className="flex-1">
                                  <p className="font-bold text-gray-800">
                                    {edu.institution}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    {edu.course_taken}
                                  </p>
                                  <p className="text-sm text-gray-500 capitalize">
                                    {edu.level_of_education
                                      ?.split('_')
                                      .join(' ')}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {edu.city || ''}
                                    {edu.city && edu.country ? ', ' : ''}
                                    {edu.country || ''}
                                  </p>
                                  <p className="text-xs text-gray-400">
                                    {edu.start_date
                                      ? new Date(
                                          edu.start_date
                                        ).toLocaleDateString('en-US', {
                                          year: 'numeric',
                                          month: 'short',
                                        })
                                      : ''}
                                    {' — '}
                                    {formatEndDate(
                                      edu.is_current,
                                      edu.end_date
                                    )}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-400 text-sm">
                            No education background recorded
                          </p>
                        )}
                      </div>

                      {/* Work Experience */}
                      <div>
                        <SectionHeader
                          title="Work Experience"
                          count={student.work_experiences?.length}
                        />
                        {student.work_experiences?.length > 0 ? (
                          <div className="space-y-3">
                            {student.work_experiences.map((work) => (
                              <div
                                key={work.id}
                                className="bg-white border border-gray-200 rounded-xl p-4 flex items-start gap-4"
                              >
                                <div className="bg-purple-50 p-2 rounded-lg">
                                  <WorkIcon sx={{ color: '#7c3aed' }} />
                                </div>
                                <div className="flex-1">
                                  <p className="font-bold text-gray-800">
                                    {work.position}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    {work.organisation}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {work.city || ''}
                                    {work.city && work.country ? ', ' : ''}
                                    {work.country || ''}
                                  </p>
                                  <p className="text-xs text-gray-400">
                                    {work.start_date
                                      ? new Date(
                                          work.start_date
                                        ).toLocaleDateString('en-US', {
                                          year: 'numeric',
                                          month: 'short',
                                        })
                                      : ''}
                                    {' — '}
                                    {formatEndDate(
                                      work.is_current,
                                      work.end_date
                                    )}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-400 text-sm">
                            No work experience recorded
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Empty State */}
      {!studentId && (
        <div className="mt-8 bg-white rounded-xl p-16 text-center shadow-sm border border-gray-100">
          <div className="bg-persian-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <PersonIcon sx={{ fontSize: 40, color: '#00999E' }} />
          </div>
          <h3 className="text-xl font-bold text-gray-800">
            Student Intelligence
          </h3>
          <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
            Enter a student UUID, student number (e.g. PLP-AIS-2602-2), or email
            address above to view their complete profile, academic performance,
            login activity, support history, and more — all in one place.
          </p>
        </div>
      )}
    </MainContent>
  );
}

export default StudentIntelligence;
